import * as z from 'zod/mini';
import {
  CREDENTIAL_NOTES_MAX_LENGTH,
  SECRET_MAX_LENGTH,
  SHORT_TEXT_MAX_LENGTH,
  SSH_KEY_MAX_LENGTH,
  SSH_PORT_MAX,
  SSH_PORT_MIN,
} from '../constants.ts';
import { timestampSchema } from './common.ts';

// A device's hidden credentials: the admin login for its web or console
// interface, and how to reach it over SSH. Stored encrypted (AES-256-GCM,
// worker/crypto/aead.ts) and returned only by the reveal endpoint, never in
// an item, tree, search, report, or history response.

const secret = z.string().check(z.maxLength(SECRET_MAX_LENGTH));
const shortText = z.string().check(z.maxLength(SHORT_TEXT_MAX_LENGTH));

/** An SSH port as typed: blank for the default, else 1–65535. */
export const sshPortSchema = z.string().check(
  z.regex(/^\d{0,5}$/u, 'Digits only'),
  z.refine(
    (port) => port === '' || (Number(port) >= SSH_PORT_MIN && Number(port) <= SSH_PORT_MAX),
    `A port is ${String(SSH_PORT_MIN)}–${String(SSH_PORT_MAX)}`,
  ),
);

export const credentialsSchema = z.object({
  username: shortText,
  password: secret,
  sshUsername: shortText,
  sshPort: sshPortSchema,
  sshPassword: secret,
  /** A private key, or a note saying which key to use. */
  sshKey: z.string().check(z.maxLength(SSH_KEY_MAX_LENGTH)),
  notes: z.string().check(z.maxLength(CREDENTIAL_NOTES_MAX_LENGTH)),
});
export type Credentials = z.infer<typeof credentialsSchema>;

export const EMPTY_CREDENTIALS: Credentials = {
  username: '',
  password: '',
  sshUsername: '',
  sshPort: '',
  sshPassword: '',
  sshKey: '',
  notes: '',
};

/** Field key → label, in form order; the history names changed fields by these, never by value. */
export const CREDENTIAL_LABELS: Readonly<Record<keyof Credentials, string>> = {
  username: 'Username',
  password: 'Password',
  sshUsername: 'SSH username',
  sshPort: 'SSH port',
  sshPassword: 'SSH password',
  sshKey: 'SSH key',
  notes: 'Notes',
};

/** The fields whose value differs, by label only. */
export function changedCredentialLabels(before: Credentials, after: Credentials): string[] {
  return (Object.keys(CREDENTIAL_LABELS) as (keyof Credentials)[])
    .filter((key) => before[key] !== after[key])
    .map((key) => CREDENTIAL_LABELS[key]);
}

/** Whether anything at all is recorded. */
export function hasAnyCredential(credentials: Credentials): boolean {
  return Object.values(credentials).some((value) => value !== '');
}

/** What the item page learns without a reveal: whether credentials exist and when they last changed. */
export const credentialStatusSchema = z.object({
  stored: z.boolean(),
  updatedAt: z.nullable(timestampSchema),
});
export type CredentialStatus = z.infer<typeof credentialStatusSchema>;

/** The reveal reply. */
export const credentialsEnvelopeSchema = z.object({ credentials: credentialsSchema });
